import axios from "axios";

import { getAccessToken, setAccessToken } from "./routes/tokenStore";

export const baseURL = process.env.REACT_APP_BACKEND;
export const appRoles = {
  editor: "editor",
  admin: "admin",
  superAdmin: "super-admin",
};

const httpService = axios.create({
  baseURL: process.env.REACT_APP_BACKEND,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// No need to store or attach tokens manually anymore
httpService.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // you can add custom headers here if needed
    return config;
  },
  (error) => Promise.reject(error),
);
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });

  failedQueue = [];
};

httpService.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue failed requests while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => httpService(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try refresh
        //await httpService.get("/refresh"); // 🚨 Make sure this matches your backend
        const oldRefreshToken = localStorage.getItem("refreshToken");

        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND}/refresh`,

          {
            headers: {
              Authorization: `Bearer ${oldRefreshToken}`,
            },
          },
        );

        const { accessToken, refreshToken } = response.data;

        setAccessToken(accessToken);
        localStorage.setItem("refreshToken", refreshToken);
        processQueue(null);
        return httpService(originalRequest);
      } catch (err) {
        processQueue(err, null);

        // 🚨 Both original + refresh failed with 401 → redirect
        if (err.response?.status === 401) {
          console.warn("Both tokens expired. Logging out...");
          window.location.href = "/"; // clear session
        }

        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    // Fallback error handling
    if (error.response) {
      return { error: error.response.data, status: error.response.status };
    }
    return { error: "Network connection lost" };
  },
);

export { httpService };
